import { isUndefined, noop } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/Button';
import { SelectInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { stateOptions } from 'utils/states';
import { SignupContextV3, withSignupMachineStateV3 } from 'domains/signupv3/Wrapper/context';

type Props = {
  defaultValue?: string;
  onSubmit: (value: string) => void;
};

const EnterState: React.FC<Props> = ({ defaultValue, onSubmit }) => {
  const [value, setValue] = useState(defaultValue);

  const { t } = useTranslation();

  const selectInputRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (selectInputRef.current) {
      selectInputRef.current.focus();
    }
  }, []);

  return (
    <>
      <Heading level="h2" className="my-64 sm-d:mb-40">
        {t('signup.steps.enterState.title')}
      </Heading>

      <SelectInput
        data-testid="state-select"
        allowEmpty
        label={t('signup.steps.enterState.stateFieldTitle')}
        options={stateOptions}
        name="state"
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => setValue(e.target.value)}
        clearValue={noop}
        setError={noop}
        setValue={noop}
        disabled={false}
        ref={selectInputRef}
      />

      <Button
        data-testid="state-submit-button"
        size="lg"
        disabled={isUndefined(value) || value.length === 0}
        className="mt-64 sm-d:mt-40 sm-d:w-full"
        onClick={() => {
          onSubmit(value as string);
        }}
      >
        {t('signup.steps.enterState.submit')}
      </Button>
    </>
  );
};

const SignupV3Wrapper = () => {
  const state = SignupContextV3.useSelector(({ context }) => context.state);
  const { send } = SignupContextV3.useActorRef();

  const onSubmit = useCallback(
    (value: string) => {
      send({
        type: 'submitState',
        state: value,
      });
    },
    [send],
  );

  return <EnterState defaultValue={state} onSubmit={onSubmit} />;
};

export const SignupV3EnterState = withSignupMachineStateV3(SignupV3Wrapper, 'enterState');
